import { BoxSlotDetailsEntity } from "@/modules/boxSlot/entities/boxSlotEntity"
import { boxSlotKeys } from "@/modules/boxSlot/gateway/boxSlotKeys"
import { SlotSubscriberEntity } from "@/modules/profile/subscriber/entities/slotSubscriberEntity"
import type { SlotSubscriberDto } from "@/modules/profile/subscriber/gateway/slotSubscriberDto"
import { ParticipantStatus } from "@/modules/profile/subscriber/subscriberTypes"
import { DEFAULT_PAGE_SIZE, type Paginated } from "@/modules/utils/paginated"
import { useServicesContext } from "@modules/core/services/services.context"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { useState } from "react"

export function useBoxSlotDetails(id: string) {
  const { boxSlotApi } = useServicesContext()
  return useSuspenseQuery({
    queryKey: boxSlotKeys.slot(id),
    queryFn: async () => {
      const result = await boxSlotApi.getSlotDetails(id)
      return BoxSlotDetailsEntity.fromDto(result)
    },
  })
}

export function useFetchBoxSlotDetails() {
  const queryClient = useQueryClient()
  const { boxSlotApi } = useServicesContext()
  async function fetchData(slotId: string) {
    return queryClient.fetchQuery({
      queryKey: boxSlotKeys.slot(slotId),
      queryFn: async () => {
        const dto = await boxSlotApi.getSlotDetails(slotId)
        return BoxSlotDetailsEntity.fromDto(dto)
      },
    })
  }
  return { fetchData }
}

export function useBoxSlotSubscribers({
  slotId,
  defaultPage,
  defaultPageSize,
  name,
  participantStatus,
}: {
  slotId: string
  defaultPage?: number
  defaultPageSize?: number
  name?: string
  participantStatus?: ParticipantStatus
}) {
  const { boxSlotApi } = useServicesContext()
  const [page, setPage] = useState(defaultPage ?? 0)
  const [pageSize, setPageSize] = useState(defaultPageSize ?? DEFAULT_PAGE_SIZE)

  const { data, isLoading, error } = useSuspenseQuery<Paginated<SlotSubscriberDto>>({
    queryKey: boxSlotKeys.subscribers(slotId, participantStatus ?? ParticipantStatus.REGISTERED, {
      name,
      page,
      pageSize,
    }),
    queryFn: async () => {
      const result = await boxSlotApi.getSlotSubscribers(slotId, page, pageSize, name, participantStatus)
      return result
    },
  })

  const handleSetPage = (newPageNumber: number) => {
    setPage(newPageNumber)
  }

  const handlePreviousPage = () => {
    if (data.page > 0) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (data.page < data.totalPages - 1) {
      setPage((prev) => prev + 1)
    }
  }

  return {
    data: data.content.map((item) => SlotSubscriberEntity.fromDto(item)),
    isLoading,
    error,
    pageSize,
    setPageSize,
    handleSetPage,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage: data.page > 0,
    hasNextPage: data.page < data.totalPages - 1,
    totalElements: data.totalElements,
  }
}
